import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChoiceTile } from '@/components/ChoiceTile'
import { CopyableCode } from '@/components/CopyableCode'
import { PublicPageShell } from '@/components/PublicPageShell'
import { cn } from '@/lib/utils'
import {
  AI_AGENTS,
  AI_COMMANDS,
  AI_LINKS,
  CLAUDE_REGISTER_OPTIONS,
  DESKTOP_OS_OPTIONS,
  SCRIPT_OS_OPTIONS,
  type AiAgentId,
  type AiIcon,
  type ClaudeRegisterOption,
  type DesktopOs,
  type ScriptOs,
} from '@/data/aiAgents'

const K = 'aiConnect'
const LINK_CLASS = 'font-medium text-(--ad-primary) underline-offset-4 hover:underline'

function ExtLink({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
      {children}
    </a>
  )
}

const TEXT_COMPONENTS = {
  b: <strong />,
  code: <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs" />,
}

/** Rich text from i18n; supports <b>, <code> and any links passed in. */
function Text({ k, links }: { k: string; links?: Record<string, string> }) {
  const linkComponents = Object.fromEntries(Object.entries(links ?? {}).map(([name, href]) => [name, <ExtLink href={href} />]))
  return <Trans i18nKey={`${K}.${k}`} components={{ ...TEXT_COMPONENTS, ...linkComponents }} />
}

function P({ k, links, muted }: { k: string; links?: Record<string, string>; muted?: boolean }) {
  return (
    <p className={cn(muted && 'text-sm text-muted-foreground')}>
      <Text k={k} links={links} />
    </p>
  )
}

/** Renders an i18n string array as a list. */
function Steps({ k, links, ordered = true }: { k: string; links?: Record<string, string>; ordered?: boolean }) {
  const { t } = useTranslation()
  const items = t(`${K}.${k}`, { returnObjects: true }) as string[]
  const List = ordered ? 'ol' : 'ul'
  return (
    <List className={cn('space-y-1 pl-6', ordered ? 'list-decimal' : 'list-disc')}>
      {items.map((_, i) => (
        <li key={i}>
          <Text k={`${k}.${i}`} links={links} />
        </li>
      ))}
    </List>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </section>
  )
}

function Icons({ icons }: { icons: AiIcon[] }) {
  return (
    <div className="flex items-center gap-2">
      {icons.map((icon) => (
        <img key={icon.src} src={icon.src} alt="" className={cn('size-9', icon.invertInDark && 'dark:invert')} />
      ))}
    </div>
  )
}

/** Grid of ChoiceTiles for a single-choice pick. */
function ChoiceGrid<T extends string>({
  options,
  value,
  onChange,
  labelKey,
  withSummary,
  columns = 2,
}: {
  options: { id: T; icons: AiIcon[] }[]
  value: T | null
  onChange: (id: T) => void
  labelKey: string
  withSummary?: boolean
  columns?: 2 | 3
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('grid gap-3', columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
      {options.map((option) => (
        <ChoiceTile
          key={option.id}
          media={<Icons icons={option.icons} />}
          title={t(`${K}.${labelKey}.${option.id}.name`)}
          description={withSummary ? t(`${K}.${labelKey}.${option.id}.summary`) : undefined}
          selected={value === option.id}
          onSelect={() => onChange(option.id)}
        />
      ))}
    </div>
  )
}

function AgentCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">{children}</CardContent>
    </Card>
  )
}

function ClaudeSetup({ onTroubleshoot }: { onTroubleshoot: () => void }) {
  const { t } = useTranslation()
  const [option, setOption] = useState<ClaudeRegisterOption>('account')
  const copyUrl = t(`${K}.copyUrl`)

  return (
    <AgentCard title={t(`${K}.agents.claude.name`)}>
      <Section title={t(`${K}.claude.installTitle`)}>
        <P k="claude.installBody" links={{ app: AI_LINKS.claudeDownload, cli: AI_LINKS.claudeCliInstall }} />
      </Section>

      <Section title={t(`${K}.claude.registerTitle`)}>
        <P k="claude.registerBody" />
        <ChoiceGrid
          options={CLAUDE_REGISTER_OPTIONS}
          value={option}
          onChange={setOption}
          labelKey="claude.options"
          withSummary
        />
        {option === 'account' && (
          <>
            <P k="claude.account.body" />
            <Steps
              k="claude.account.where"
              ordered={false}
              links={{ personal: AI_LINKS.claudePersonalConnector, team: AI_LINKS.claudeTeamConnector }}
            />
            <P k="claude.account.nameIt" />
            <CopyableCode code={AI_COMMANDS.mcpUrl} copyLabel={copyUrl} />
            <P k="claude.account.after" />
          </>
        )}
        {option === 'cli' && (
          <>
            <P k="claude.cli.body" />
            <CopyableCode code={AI_COMMANDS.claudeCli} copyLabel={t(`${K}.copyCommand`)} />
          </>
        )}
        {option === 'windows' && (
          <>
            <P k="claude.windows.body" />
            <Steps k="claude.windows.steps" />
            <CopyableCode code={AI_COMMANDS.mcpUrl} copyLabel={copyUrl} />
          </>
        )}
        {option === 'mac' && <P k="claude.mac.body" />}
      </Section>

      <Section title={t(`${K}.claude.connectTitle`)}>
        <P k="claude.connectApp" />
        <P k="claude.connectCli" />
        <P k="claude.connectBrowser" />
        <P k="multiNetwork" muted />
        <p>
          <Trans
            i18nKey={`${K}.claude.troubleshootingLink`}
            components={{
              action: (
                <a
                  href="#"
                  role="button"
                  className={LINK_CLASS}
                  onClick={(e) => {
                    e.preventDefault()
                    onTroubleshoot()
                  }}
                />
              ),
            }}
          />
        </p>
      </Section>
    </AgentCard>
  )
}

function ClaudeTroubleshooting({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  return (
    <AgentCard title={t(`${K}.troubleshooting.title`)}>
      <Button variant="outline" size="sm" className="w-fit" onClick={onBack}>
        <ArrowLeft />
        {t(`${K}.troubleshooting.back`)}
      </Button>
      <Section title={t(`${K}.troubleshooting.intro`)}>
        <Steps k="troubleshooting.steps" links={{ connectors: AI_LINKS.claudeConnectors }} />
        <P k="troubleshooting.restart" />
      </Section>
    </AgentCard>
  )
}

function CodexSetup() {
  const { t } = useTranslation()
  return (
    <AgentCard title={t(`${K}.agents.codex.name`)}>
      <Section title={t(`${K}.codex.installTitle`)}>
        <P k="codex.installBody" links={{ cli: AI_LINKS.codexCliInstall }} />
      </Section>

      <Section title={t(`${K}.codex.addTitle`)}>
        <P k="codex.addBody" />
        <CopyableCode code={AI_COMMANDS.codexAdd} copyLabel={t(`${K}.copyCommand`)} />
        <P k="codex.browser" />
        <P k="multiNetwork" muted />
      </Section>

      <Section title={t(`${K}.codex.askTitle`)}>
        <P k="codex.askBody" />
      </Section>

      <Section title={t(`${K}.codex.appTitle`)}>
        <P k="codex.appBody" links={{ app: AI_LINKS.codexAppInstall }} />
        <P k="codex.appSkipped" />
        <Steps k="codex.appSteps" />
        <CopyableCode code={AI_COMMANDS.mcpUrl} copyLabel={t(`${K}.copyUrl`)} />
        <P k="codex.appAuthorise" />
      </Section>
    </AgentCard>
  )
}

function AntigravitySetup() {
  const { t } = useTranslation()
  const [os, setOs] = useState<DesktopOs>('mac')
  const command = os === 'windows' ? AI_COMMANDS.antigravityWindows : AI_COMMANDS.antigravityUnix

  return (
    <AgentCard title={t(`${K}.agents.antigravity.name`)}>
      <Section title={t(`${K}.antigravity.installTitle`)}>
        <Steps k="antigravity.installSteps" links={{ app: AI_LINKS.antigravityDownload }} />
      </Section>

      <Section title={t(`${K}.antigravity.addTitle`)}>
        <P k="antigravity.addBody" />
        <ChoiceGrid options={DESKTOP_OS_OPTIONS} value={os} onChange={setOs} labelKey="os" columns={3} />
        <P k={`antigravity.run.${os}`} />
        <CopyableCode code={command} copyLabel={t(`${K}.copyCommand`)} />
      </Section>

      <Section title={t(`${K}.antigravity.authTitle`)}>
        <Steps k="antigravity.authSteps" />
        <P k="antigravity.done" />
        <P k="multiNetwork" muted />
      </Section>
    </AgentCard>
  )
}

function MistralSetup() {
  const { t } = useTranslation()
  const [os, setOs] = useState<ScriptOs>('unix')
  const command = os === 'windows' ? AI_COMMANDS.mistralWindows : AI_COMMANDS.mistralUnix

  return (
    <AgentCard title={t(`${K}.agents.mistral.name`)}>
      <Section title={t(`${K}.mistral.title`)}>
        <P k="mistral.body" />
        <ChoiceGrid options={SCRIPT_OS_OPTIONS} value={os} onChange={setOs} labelKey="os" />
        <P k={`mistral.run.${os}`} />
        <CopyableCode code={command} copyLabel={t(`${K}.copyCommand`)} />
      </Section>
    </AgentCard>
  )
}

/** Public page (/ai) explaining how to connect an AI agent to the Adnuntius MCP server. */
export function AiConnectPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const [troubleshooting, setTroubleshooting] = useState(false)
  const agent = AI_AGENTS.find((a) => a.id === params.get('agent'))?.id ?? null

  const selectAgent = (id: AiAgentId | null) => {
    setTroubleshooting(false)
    setParams(id ? { agent: id } : {})
  }

  return (
    <PublicPageShell>
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">{t(`${K}.title`)}</h1>

        {!agent ? (
          <>
            <P k="intro" />
            <P k="access" />
            <P k="multiNetworkIntro" muted />
            <ChoiceGrid options={AI_AGENTS} value={null} onChange={selectAgent} labelKey="agents" withSummary />
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => selectAgent(null)}>
              <ArrowLeft />
              {t(`${K}.chooseDifferent`)}
            </Button>
            {agent === 'claude' &&
              (troubleshooting ? (
                <ClaudeTroubleshooting onBack={() => setTroubleshooting(false)} />
              ) : (
                <ClaudeSetup onTroubleshoot={() => setTroubleshooting(true)} />
              ))}
            {agent === 'codex' && <CodexSetup />}
            {agent === 'antigravity' && <AntigravitySetup />}
            {agent === 'mistral' && <MistralSetup />}
          </>
        )}
      </div>
    </PublicPageShell>
  )
}
